import React, { useState, useEffect, useCallback } from 'react';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Megaphone, Loader2, Lock, CheckCircle, ArrowLeft, Plus } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '@/App';

import { stripePromise } from '@/lib/stripe';

const STATUS_LABELS = {
  pending_payment: { text: 'Awaiting payment', cls: 'bg-amber-500/20 text-amber-300' },
  paid_pending_approval: { text: 'Pending approval', cls: 'bg-blue-500/20 text-blue-300' },
  approved: { text: 'Live', cls: 'bg-emerald-500/20 text-emerald-300' },
  rejected: { text: 'Rejected', cls: 'bg-red-500/20 text-red-300' },
  expired: { text: 'Expired', cls: 'bg-gray-500/20 text-gray-400' },
  canceled: { text: 'Canceled', cls: 'bg-gray-500/20 text-gray-400' },
};

function PayForm({ amount, onPaid, onError }) {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setProcessing(true);
    const { error, paymentIntent } = await stripe.confirmPayment({
      elements,
      // Required by Stripe at confirmation, even though card payments don't redirect.
      confirmParams: { return_url: `${window.location.origin}${window.location.pathname}` },
      redirect: 'if_required',
    });
    if (error) { onError?.(error.message); setProcessing(false); return; }
    if (paymentIntent && paymentIntent.status === 'succeeded') onPaid?.();
    else { onError?.('Payment not completed'); setProcessing(false); }
  };

  return (
    <form onSubmit={submit} data-testid="promo-pay-form">
      <div className="mb-4 p-4 rounded-xl bg-white/5 border border-white/10"><PaymentElement options={{ layout: 'tabs' }} /></div>
      <Button type="submit" disabled={!stripe || processing} className="w-full bg-amber-600 hover:bg-amber-700 text-white py-6" data-testid="promo-pay-now-btn">
        {processing ? <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Processing…</> : <><Lock className="w-4 h-4 mr-2" /> Pay ${amount?.toFixed(2)}</>}
      </Button>
    </form>
  );
}

export default function PromoteSpotlightButton({ className = '' }) {
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const [open, setOpen] = useState(false);
  const [step, setStep] = useState('list'); // list | form | pay | done
  const [options, setOptions] = useState(null);
  const [mine, setMine] = useState([]);
  const EMPTY_FORM = { entity: '', slot: '', billing_period: 'day', duration_days: 7, duration_months: 1, title: '', body: '', image_url: '' };
  const [form, setForm] = useState(EMPTY_FORM);
  const [clientSecret, setClientSecret] = useState(null);
  const [amount, setAmount] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const [o, m] = await Promise.all([
        axios.get(`${API}/spotlight-promotions/options`, { headers }),
        axios.get(`${API}/spotlight-promotions/mine`, { headers }),
      ]);
      setOptions(o.data);
      setMine(m.data.promotions || []);
    } catch (err) { /* vendor-gated */ }
  }, []);

  useEffect(() => { if (open) refresh(); }, [open, refresh]);

  const allEntities = options ? [
    ...(options.entities.storefront ? [options.entities.storefront] : []),
    ...options.entities.workshops,
    ...options.entities.products,
  ] : [];

  const selectedSlot = options?.slots.find((s) => s.key === form.slot);
  const monthlyAvailable = (selectedSlot?.price_per_month || 0) > 0;
  const price = !selectedSlot ? 0
    : form.billing_period === 'month'
      ? selectedSlot.price_per_month * form.duration_months
      : selectedSlot.price_per_day * form.duration_days;

  const pickEntity = (val) => {
    const [type, id] = val.split('::');
    const ent = allEntities.find((e) => e.entity_type === type && e.entity_id === id);
    setForm((f) => ({ ...f, entity: val, title: f.title || ent?.title || '', image_url: f.image_url || ent?.image_url || '' }));
  };

  const submit = async () => {
    if (!form.entity || !form.slot || !form.title.trim()) { toast.error('Pick an item, a placement, and a headline'); return; }
    const [entity_type, entity_id] = form.entity.split('::');
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/spotlight-promotions`, {
        entity_type, entity_id, slot: form.slot,
        billing_period: form.billing_period,
        duration_days: form.duration_days, duration_months: form.duration_months,
        title: form.title, body: form.body, image_url: form.image_url,
      }, { headers });
      setClientSecret(res.data.client_secret);
      setAmount(res.data.amount);
      setStep('pay');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not start promotion');
    } finally { setSubmitting(false); }
  };

  const resetToList = () => { setStep('list'); setClientSecret(null); setForm(EMPTY_FORM); refresh(); };

  return (
    <>
      <Button className={`bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white ${className}`} onClick={() => setOpen(true)} data-testid="open-promote-btn">
        <Megaphone className="w-4 h-4 mr-2" /> Advertise / Promote
      </Button>

      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setStep('list'); }}>
        <DialogContent className="max-w-lg max-h-[88vh] overflow-y-auto" data-testid="promote-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Megaphone className="w-5 h-5 text-amber-600" /> Promote with a Spotlight</DialogTitle>
          </DialogHeader>

          {/* LIST */}
          {step === 'list' && (
            <div className="space-y-4">
              {options && !options.has_vendor && (
                <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3">An approved storefront is required to promote.</p>
              )}
              <Button onClick={() => setStep('form')} disabled={!options?.has_vendor} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="new-promotion-btn">
                <Plus className="w-4 h-4 mr-1" /> New Promotion
              </Button>
              <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-2">Your Promotions</h4>
                {mine.length === 0 ? <p className="text-sm text-gray-400">No promotions yet.</p> : (
                  <div className="space-y-2">
                    {mine.map((p) => {
                      const st = STATUS_LABELS[p.status] || { text: p.status, cls: 'bg-gray-200 text-gray-700' };
                      return (
                        <div key={p.id} className="flex items-center justify-between border rounded-lg p-2" data-testid={`my-promo-${p.id}`}>
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-gray-800 truncate">{p.title}</p>
                            <p className="text-xs text-gray-400">{p.slot} · {p.run_label || `${p.duration_days}d`} · ${p.price}</p>
                          </div>
                          <span className={`text-[10px] px-2 py-0.5 rounded-full ${st.cls}`}>{st.text}</span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* FORM */}
          {step === 'form' && options && (
            <div className="space-y-4">
              <div>
                <Label className="text-xs text-gray-500">What to promote</Label>
                <Select value={form.entity} onValueChange={pickEntity}>
                  <SelectTrigger className="mt-1" data-testid="promo-entity-select"><SelectValue placeholder="Choose your storefront, workshop, or product" /></SelectTrigger>
                  <SelectContent>
                    {allEntities.map((e) => (
                      <SelectItem key={`${e.entity_type}::${e.entity_id}`} value={`${e.entity_type}::${e.entity_id}`}>
                        [{e.entity_type}] {e.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-gray-500">Placement</Label>
                <Select value={form.slot} onValueChange={(v) => setForm({ ...form, slot: v })}>
                  <SelectTrigger className="mt-1" data-testid="promo-slot-select"><SelectValue placeholder="Where should it show?" /></SelectTrigger>
                  <SelectContent>
                    {options.slots.map((s) => (
                      <SelectItem key={s.key} value={s.key}>
                        {s.label} — ${s.price_per_day}/day{(s.price_per_month || 0) > 0 ? ` or $${s.price_per_month}/month` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {selectedSlot?.image && (
                <p className="text-xs text-gray-500 bg-gray-50 border rounded-lg p-2.5" data-testid="promo-image-spec">
                  <strong>Artwork for this spot:</strong> {selectedSlot.image.width}×{selectedSlot.image.height}px
                  ({selectedSlot.image.ratio}), under {selectedSlot.image.max_kb}KB. {selectedSlot.image.note}
                </p>
              )}
              <div>
                <Label className="text-xs text-gray-500">How long?</Label>
                <div className="flex gap-2 mt-1">
                  <Button type="button" size="sm" variant={form.billing_period === 'day' ? 'default' : 'outline'}
                    onClick={() => setForm({ ...form, billing_period: 'day' })}
                    className={form.billing_period === 'day' ? 'bg-amber-600 hover:bg-amber-700 text-white' : ''}
                    data-testid="promo-period-day">By the day</Button>
                  <Button type="button" size="sm" variant={form.billing_period === 'month' ? 'default' : 'outline'}
                    onClick={() => setForm({ ...form, billing_period: 'month' })}
                    disabled={!monthlyAvailable}
                    className={form.billing_period === 'month' ? 'bg-amber-600 hover:bg-amber-700 text-white' : ''}
                    data-testid="promo-period-month">By the month</Button>
                </div>
                {form.billing_period === 'month' ? (
                  <Input type="number" min={options.min_months || 1} max={options.max_months || 12} value={form.duration_months}
                    onChange={(e) => setForm({ ...form, duration_months: Math.max(1, Math.min(options.max_months || 12, parseInt(e.target.value) || 1)) })}
                    className="mt-2" data-testid="promo-months-input" />
                ) : (
                  <Input type="number" min={options.min_days} max={options.max_days} value={form.duration_days}
                    onChange={(e) => setForm({ ...form, duration_days: Math.max(options.min_days, Math.min(options.max_days, parseInt(e.target.value) || 1)) })}
                    className="mt-2" data-testid="promo-duration-input" />
                )}
                <p className="text-[11px] text-gray-400 mt-1">
                  {form.billing_period === 'month'
                    ? `Runs for ${form.duration_months * 30} days.`
                    : 'Runs from the moment it is approved.'}
                </p>
              </div>
              <div>
                <Label className="text-xs text-gray-500">Headline</Label>
                <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1" data-testid="promo-title-input" />
              </div>
              <div>
                <Label className="text-xs text-gray-500">Short text (optional)</Label>
                <Textarea value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} rows={2} className="mt-1" />
              </div>
              <div>
                <Label className="text-xs text-gray-500">Image URL (optional)</Label>
                <Input value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} placeholder="https://…" className="mt-1" />
              </div>
              <div className="flex items-center justify-between bg-amber-50 border border-amber-200 rounded-lg p-3">
                <span className="text-sm text-gray-700">Total</span>
                <span className="text-lg font-bold text-amber-700" data-testid="promo-price">${price.toFixed(2)}</span>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setStep('list')}><ArrowLeft className="w-4 h-4 mr-1" /> Back</Button>
                <Button onClick={submit} disabled={submitting} className="bg-amber-600 hover:bg-amber-700 text-white flex-1" data-testid="promo-continue-btn">
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null} Continue to Payment
                </Button>
              </div>
            </div>
          )}

          {/* PAY */}
          {step === 'pay' && clientSecret && (
            <div className="space-y-3">
              <p className="text-sm text-gray-500">Pay ${amount.toFixed(2)} to submit your spotlight for approval.</p>
              <Elements stripe={stripePromise} options={{ clientSecret, appearance: { theme: 'stripe', variables: { colorPrimary: '#d97706' } } }}>
                <PayForm amount={amount} onPaid={() => setStep('done')} onError={(m) => toast.error(m)} />
              </Elements>
            </div>
          )}

          {/* DONE */}
          {step === 'done' && (
            <div className="text-center py-6" data-testid="promo-done">
              <CheckCircle className="w-14 h-14 text-emerald-500 mx-auto mb-3" />
              <h3 className="text-lg font-bold text-gray-800">Submitted for approval</h3>
              <p className="text-sm text-gray-500 mt-1">Your spotlight goes live once an admin approves it.</p>
              <Button onClick={resetToList} className="mt-4 bg-amber-600 hover:bg-amber-700 text-white">Done</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
